import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  Chip,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import styled from "styled-components/macro";
import { useHistory } from "react-router-dom";
import { useAppContext } from "../../api/AppContext";
import { useSnackbar } from "notistack";
import { Autocomplete } from "@material-ui/lab";

const Section = styled(Paper)`
  padding: 16px;
`;

const CompanyCard = styled(Box)`
  padding: 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ImageGalleryStart = () => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const {
    fetchMyAuthCompanies,
    isMyAuthCompaniesLoading,
    createImageGallery,
    listImageFields,
  } = useAppContext();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [companyDetails, setCompanyDetails] = useState([]);
  const [companiesLocal, setCompaniesLocal] = useState([]);
  const [isCompaniesLoadingLocal, setIsCompaniesLoadingLocal] = useState(false);
  const [fieldsOptions, setFieldsOptions] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [isPublished, setIsPublished] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setIsCompaniesLoadingLocal(true);
        const list = await fetchMyAuthCompanies?.();
        if (mounted) setCompaniesLocal(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("list my auth companies failed", e);
        enqueueSnackbar(e?.message || "Failed to load companies", {
          variant: "error",
          autoHideDuration: 4000,
        });
      } finally {
        if (mounted) setIsCompaniesLoadingLocal(false);
      }
    };
    if (!companiesLocal?.length) load();
    return () => {
      mounted = false;
    };
  }, [companiesLocal?.length, fetchMyAuthCompanies, enqueueSnackbar]);

  useEffect(() => {
    let mounted = true;
    const loadFields = async () => {
      try {
        const list = await listImageFields?.();
        if (mounted) setFieldsOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("Failed to load fields", e);
      }
    };
    loadFields();
    return () => {
      mounted = false;
    };
  }, [listImageFields]);

  const canCreate = useMemo(
    () => title.trim() && companyDetails.length > 0,
    [title, companyDetails]
  );

  const handleCreate = useCallback(async () => {
    setIsSaving(true);
    try {
      const payload = {
        name: title.trim(),
        description: description || "",
        companies: companyDetails.map((c) => ({
          id: c.id,
          accessType: c.accessType,
        })),
        ...(selectedFields.length
          ? { fieldIds: selectedFields.map((f) => f.id) }
          : {}),
        isPublished,
      };

      await createImageGallery(payload);

      enqueueSnackbar("Gallery created", {
        variant: "success",
        autoHideDuration: 3000,
      });
      history.push("/image-galleries");
    } catch (e) {
      console.error("Create gallery failed", e);
      enqueueSnackbar(e?.message || "Failed to create gallery", {
        variant: "error",
        autoHideDuration: 4000,
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    companyDetails,
    createImageGallery,
    description,
    enqueueSnackbar,
    history,
    title,
    selectedFields,
    isPublished,
  ]);

  return (
    <Box boxSizing="border-box" p={4} width="100%">
      <Grid container direction="column" spacing={3}>
        <Grid item>
          <Typography variant="h6">Create a new Image Gallery</Typography>
          <Typography color="textSecondary" variant="body2">
            Choose company, provide a title and description. You can add images
            later.
          </Typography>
        </Grid>

        <Grid item>
          <Section>
            <Grid container spacing={2}>
              <Grid item md={6} xs={12}>
                <TextField
                  autoFocus
                  fullWidth
                  label="Title"
                  value={title}
                  variant="outlined"
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <Autocomplete
                  multiple
                  getOptionLabel={(option) =>
                    option?.name || option?.cname || option?.id || ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  loading={isMyAuthCompaniesLoading || isCompaniesLoadingLocal}
                  options={companiesLocal}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        label="Companies"
                        placeholder="Select one or more companies"
                        variant="outlined"
                      />
                    );
                  }}
                  // Keep state as a lightweight array of { id, accessType }
                  // and derive the Autocomplete "value" from companiesLocal for correct labels
                  value={companiesLocal.filter((opt) =>
                    (companyDetails || []).some((c) => c.id === opt.id)
                  )}
                  onChange={(e, selectedOptions) => {
                    const updatedDetails = (selectedOptions || []).map(
                      (opt) => {
                        const existing = (companyDetails || []).find(
                          (c) => c.id === opt.id
                        );
                        return {
                          id: opt.id,
                          accessType: existing?.accessType || "read",
                        };
                      }
                    );
                    setCompanyDetails(updatedDetails);
                  }}
                />
              </Grid>

              {fieldsOptions?.length > 0 && (
                <Grid item md={6} xs={12}>
                  <Autocomplete
                    multiple
                    getOptionLabel={(option) =>
                      option?.name || option?.id || ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    noOptionsText="No field available"
                    options={fieldsOptions}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Disabled Fields (optional)"
                        placeholder="Select fields to disable for this gallery."
                        variant="outlined"
                      />
                    )}
                    value={selectedFields}
                    onChange={(_, val) => setSelectedFields(val || [])}
                  />
                </Grid>
              )}

              {companyDetails.length > 0 && (
                <Grid item xs={12}>
                  <Typography gutterBottom variant="subtitle1">
                    Company Access Settings
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {companyDetails.map((c, idx) => (
                      <CompanyCard key={c.id}>
                        <Grid container alignItems="center" spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="subtitle2">
                              <Chip
                                label={
                                  companiesLocal.find((x) => x.id === c.id)
                                    ?.name ||
                                  companiesLocal.find((x) => x.id === c.id)
                                    ?.cname ||
                                  c.name ||
                                  c.cname ||
                                  c.id
                                }
                                size="small"
                                style={{ marginLeft: 8 }}
                              />
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              select
                              SelectProps={{ native: true }}
                              label="Access Type"
                              size="small"
                              value={c.accessType}
                              variant="outlined"
                              onChange={(e) => {
                                const newDetails = [...companyDetails];
                                newDetails[idx].accessType = e.target.value;
                                setCompanyDetails(newDetails);
                              }}
                            >
                              <option value="read">Read</option>
                              <option value="write">Read & Write</option>
                            </TextField>
                          </Grid>
                        </Grid>
                      </CompanyCard>
                    ))}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  label="Description"
                  minRows={3}
                  value={description}
                  variant="outlined"
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isPublished}
                      color="primary"
                      onChange={(e) => setIsPublished(e.target.checked)}
                    />
                  }
                  label="Publish now"
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" gap={8}>
                  <Button
                    color="primary"
                    disabled={!canCreate || isSaving}
                    variant="contained"
                    onClick={handleCreate}
                  >
                    Create
                  </Button>
                  <Button onClick={() => history.push("/image-galleries")}>
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Section>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ImageGalleryStart;
